/**
 * Main App Component - UI
 * 
 * Root component handling routing and layout for the public website.
 * Provides header and footer layout with all public pages.
 * 
 * @version 1.0.0
 * @author NGO Platform Team
 */

import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';

// Components
import Header from './components/Header';
import Footer from './components/Footer';

// Pages
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import FoundersPage from './pages/FoundersPage';
import TeamPage from './pages/TeamPage';
import CampaignsPage from './pages/CampaignsPage';
import CampaignDetailPage from './pages/CampaignDetailPage';
import SubmitCampaignPage from './pages/SubmitCampaignPage';
import GalleryPage from './pages/GalleryPage';
import GalleryDetailPage from './pages/GalleryDetailPage';
import VolunteerPage from './pages/VolunteerPage';
import CSRPage from './pages/CSRPage';
import ContactPage from './pages/ContactPage';
import AwardsPage from './pages/AwardsPage';
import DonationSuccessPage from './pages/DonationSuccessPage';
import LegalPage from './pages/LegalPage';
import FinancialLegalDocumentsPage from './pages/FinancialLegalDocumentsPage';

/**
 * App Component
 * Main application router and layout
 */
function App() {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Initialize app
    setIsLoading(false);
  }, []);

  if (isLoading) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
      }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Router>
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', backgroundColor: '#fff', overflowX: 'hidden' }}>
        <Header />
        <Box sx={{ flex: 1, backgroundColor: '#fff', overflowX: 'hidden' }}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/founders" element={<FoundersPage />} />
            <Route path="/team" element={<TeamPage />} />
            <Route path="/campaigns" element={<CampaignsPage />} />
            <Route path="/campaigns/submit" element={<SubmitCampaignPage />} />
            <Route path="/campaign/:slug" element={<CampaignDetailPage />} />
            <Route path="/gallery" element={<GalleryPage />} />
            <Route path="/gallery/:id" element={<GalleryDetailPage />} />
            <Route path="/volunteer" element={<VolunteerPage />} />
            <Route path="/csr" element={<CSRPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/awards" element={<AwardsPage />} />
            <Route path="/donation/status" element={<DonationSuccessPage />} />
            <Route path="/financial-legal-documents" element={<FinancialLegalDocumentsPage />} />
            <Route path="/terms" element={<LegalPage />} />
            <Route path="/privacy" element={<LegalPage />} />
            <Route path="/refund" element={<LegalPage />} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </Box>
        <Footer />
      </Box>
    </Router>
  );
}

export default App;
